import { useCallback, useState } from 'react';
import { ArrowLeft, Lock } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import MatrixRainBackground from '../components/auth/MatrixRainBackground';

const MIN_RESET_PASSWORD_LENGTH = 8;

function validateResetPassword(password, confirmPassword) {
  if (!password || !confirmPassword) {
    return 'Veuillez remplir les deux champs.';
  }
  if (password.length < MIN_RESET_PASSWORD_LENGTH) {
    return 'Le mot de passe doit contenir au moins 8 caractères.';
  }
  if (password !== confirmPassword) {
    return 'Les mots de passe ne correspondent pas.';
  }
  return null;
}

function getResetPasswordErrorMessage(error) {
  return error.response?.data?.detail || 'Une erreur est survenue.';
}

function useResetPasswordForm(token) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setErrorMessage('');

    const validationError = validateResetPassword(password, confirmPassword);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post('/api/auth/reset-password', { token, new_password: password });
      setSuccess(true);
    } catch (error) {
      setErrorMessage(getResetPasswordErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [confirmPassword, password, token]);

  return {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    isSubmitting,
    success,
    errorMessage,
    handleSubmit,
  };
}

function ResetPasswordInvalidLink() {
  return (
    <div className="login-page-bg login-matrix-shell flex min-h-screen items-center justify-center px-4 py-8" data-testid="reset-password-page">
      <MatrixRainBackground />
      <div className="login-page-content w-full max-w-sm">
        <div className="login-card w-full rounded-lg p-8 text-center">
          <p className="mb-4 text-zinc-300">Lien invalide ou expiré.</p>
          <Link to="/forgot-password" className="text-sm text-zinc-300 underline hover:opacity-80" data-testid="request-new-link">
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordSuccess() {
  return (
    <div data-testid="reset-password-success">
      <div className="rounded-md border border-emerald-800/50 bg-emerald-900/30 p-4 text-sm text-emerald-300">Mot de passe mis à jour avec succès.</div>
      <Link to="/login" className="mt-4 block text-center text-sm text-zinc-300 underline hover:opacity-80" data-testid="go-to-login-after-reset">
        Se connecter avec le nouveau mot de passe
      </Link>
    </div>
  );
}

function ResetPasswordForm({ formState }) {
  return (
    <form onSubmit={formState.handleSubmit} className="space-y-4" data-testid="reset-password-form">
      <div className="relative">
        <Lock className="pointer-events-none absolute left-4 top-4 h-6 w-6 text-zinc-500" />
        <Input data-testid="reset-password-input" type="password" value={formState.password} onChange={(event) => formState.setPassword(event.target.value)} placeholder="Nouveau mot de passe" autoComplete="new-password" className="h-14 border-zinc-800 bg-zinc-900/50 pl-12 text-white placeholder:text-zinc-600 focus-visible:border-white focus-visible:ring-0" />
      </div>
      <div className="relative">
        <Lock className="pointer-events-none absolute left-4 top-4 h-6 w-6 text-zinc-500" />
        <Input data-testid="reset-password-confirm-input" type="password" value={formState.confirmPassword} onChange={(event) => formState.setConfirmPassword(event.target.value)} placeholder="Confirmer le mot de passe" autoComplete="new-password" className="h-14 border-zinc-800 bg-zinc-900/50 pl-12 text-white placeholder:text-zinc-600 focus-visible:border-white focus-visible:ring-0" />
      </div>
      {formState.errorMessage ? <p data-testid="reset-password-error" className="text-sm text-rose-400">{formState.errorMessage}</p> : null}
      <Button data-testid="reset-password-submit-button" type="submit" disabled={formState.isSubmitting} className="h-14 w-full rounded-sm bg-white text-base font-semibold text-black transition-transform duration-200 hover:scale-[1.01] hover:bg-zinc-200">
        {formState.isSubmitting ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
      </Button>
    </form>
  );
}

function ResetPasswordCard({ formState }) {
  return (
    <div className="login-card w-full rounded-lg p-8">
      <Link to="/login" className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-white" data-testid="back-to-login-from-reset">
        <ArrowLeft className="h-4 w-4" />
        Retour à la connexion
      </Link>
      <h1 className="mb-2 font-['Playfair_Display'] text-2xl leading-tight text-white" data-testid="reset-password-title">
        Nouveau mot de passe
      </h1>
      <p className="mb-6 text-sm text-zinc-400">Choisissez un nouveau mot de passe sécurisé (8 caractères min.).</p>
      {formState.success ? <ResetPasswordSuccess /> : <ResetPasswordForm formState={formState} />}
    </div>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const formState = useResetPasswordForm(token);

  if (!token) {
    return <ResetPasswordInvalidLink />;
  }

  return (
    <div className="login-page-bg login-matrix-shell flex min-h-screen items-center justify-center px-4 py-8" data-testid="reset-password-page">
      <MatrixRainBackground />
      <div className="login-page-content w-full max-w-sm">
        <div className="mb-10 text-center">
          <img src="/logo-braux-white.png" alt="Braux Paysagistes" className="mx-auto h-20 w-auto" />
        </div>
        <ResetPasswordCard formState={formState} />
      </div>
    </div>
  );
}
